import React, { useCallback, useEffect, useState } from "react";
import CardTable from "../../../components/AdminTable/CardTable";
import axios from "axios";
import { apiKey } from "../../../config/config";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaEdit, FaTrashAlt, FaPrint } from "react-icons/fa";
import { languageHelper } from "../../../config/helperFunctions";
import Dropdown from "../../../components/AdminDropdown/Dropdown";
import GiftCardPrintModal from "../../../components/GiftCard/GiftCardPrintModal";

function GiftCards() {
  const user = JSON.parse(localStorage.getItem("rms-admin"));
  const currency = user?.user?.currency || "SAR";
  const [data, setData] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [printCard, setPrintCard] = useState(null);

  const navigate = useNavigate();

  const fetchGiftCards = useCallback(() => {
    axios
      .get(`${apiKey}/api/rms/admin/gift-cards`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
        params: {
          outletId: user?.user?.outlet_id,
        },
      })
      .then((res) => {
        if (res.data?.success) {
          setData(res.data.data || []);
        }
      })
      .catch((err) => {
        console.error("Fetch gift cards error:", err);
      });
  }, [user?.token, user?.user?.outlet_id]);

  useEffect(() => {
    fetchGiftCards();
  }, [fetchGiftCards]);

  const handleDelete = (id) => {
    axios
      .delete(`${apiKey}/api/rms/admin/gift-cards/${id}`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      })
      .then((res) => {
        toast.success(res.data?.message || "Gift card deleted successfully!", {
          position: "bottom-right",
          theme: "colored",
        });
        fetchGiftCards();
        setDeleteId(null);
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Failed to delete gift card", {
          position: "bottom-right",
          theme: "colored",
        });
      });
  };

  const dropdownOptions = [
    {
      name: languageHelper("Print"),
      icon: <FaPrint className="text-purple-600" />,
      func: (id) => {
        const card = data.find((c) => c.id === id);
        if (card) setPrintCard(card);
      },
    },
    {
      name: languageHelper("Edit"),
      icon: <FaEdit className="text-blue-600" />,
      func: (id) => {
        navigate(`AddGiftCardForm/${id}`);
      },
    },
    {
      name: languageHelper("Delete"),
      icon: <FaTrashAlt className="text-red-600" />,
      func: (id) => {
        setDeleteId(id);
      },
    },
  ];

  const columns = [
    {
      header: languageHelper("SN") || "SN",
      cell: (sn) => sn.row.index + 1,
    },
    {
      header: languageHelper("Code") || "Code",
      accessorKey: "code",
      cell: (row) => (
        <span className="font-mono font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded border border-purple-200">
          {row.getValue()}
        </span>
      ),
    },
    {
      header: languageHelper("Title") || "Title",
      accessorKey: "title",
      cell: (row) => (
        <span className="font-semibold text-gray-800">
          {row.getValue() || "-"}
        </span>
      ),
    },
    {
      header: languageHelper("Price") || "Price",
      accessorKey: "price",
      cell: (row) => (
        <span className="text-gray-700 font-medium">
          {currency} {Number(row.getValue() || 0).toFixed(2)}
        </span>
      ),
    },
    {
      header: languageHelper("Value") || "Value",
      accessorKey: "value",
      cell: (row) => (
        <span className="text-emerald-700 font-bold">
          {currency} {Number(row.getValue() || 0).toFixed(2)}
        </span>
      ),
    },
    {
      header: languageHelper("Used") || "Used",
      accessorKey: "used_amount",
      cell: (row) => (
        <span className="text-amber-700 font-medium">
          {currency} {Number(row.getValue() || 0).toFixed(2)}
        </span>
      ),
    },
    {
      header: languageHelper("Remaining") || "Remaining",
      cell: (row) => {
        const remaining = row.row.original.remaining_balance ?? (row.row.original.value - row.row.original.used_amount);
        return (
          <span className="font-bold text-blue-700">
            {currency} {Number(remaining || 0).toFixed(2)}
          </span>
        );
      },
    },
    {
      header: languageHelper("Customer") || "Customer",
      cell: (row) => {
        const cust = row.row.original.customer;
        return cust ? (
          <span className="text-gray-800 font-medium">{cust.name}</span>
        ) : (
          <span className="text-gray-400 italic text-xs">
            {languageHelper("All Customers")}
          </span>
        );
      },
    },
    {
      header: languageHelper("Expiration Date") || "Expiration Date",
      accessorKey: "expiration_date",
      cell: (row) => {
        const dateStr = row.getValue() ? row.getValue().split("T")[0] : "-";
        const isExp = row.row.original.is_expired;
        return (
          <span className={isExp ? "text-red-600 font-semibold text-xs" : "text-gray-700 text-xs"}>
            {dateStr} {isExp && `(${languageHelper("Expired")})`}
          </span>
        );
      },
    },
    {
      header: languageHelper("Status") || "Status",
      accessorKey: "status",
      cell: (row) => {
        const status = row.getValue();
        if (status === "active") {
          return (
            <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded text-xs font-semibold">
              {languageHelper("Active")}
            </span>
          );
        } else if (status === "redeemed") {
          return (
            <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-semibold">
              {languageHelper("Redeemed")}
            </span>
          );
        } else if (status === "expired") {
          return (
            <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-xs font-semibold">
              {languageHelper("Expired")}
            </span>
          );
        } else {
          return (
            <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-semibold">
              {languageHelper("Inactive")}
            </span>
          );
        }
      },
    },
    {
      header: languageHelper("Actions") || "Actions",
      cell: (row) => (
        <Dropdown id={row.row.original.id} options={dropdownOptions} />
      ),
    },
  ];

  const buttonData = [
    {
      buttonText: languageHelper("Add Gift Card"),
      path: "AddGiftCardForm",
    },
  ];

  return (
    <div>
      <CardTable
        buttonData={buttonData}
        data={data}
        columns={columns}
        heading={languageHelper("Gift Cards")}
      />

      {/* Printable Card Modal */}
      {printCard && (
        <GiftCardPrintModal
          card={printCard}
          currency={currency}
          onClose={() => setPrintCard(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteId && (
        <div
          className="w-full h-screen grid place-items-center fixed top-0 left-0 bg-black bg-opacity-50 backdrop-blur-md p-4 z-50"
          onClick={() => setDeleteId(null)}
        >
          <div
            className="p-6 rounded-lg bg-white max-w-md w-full space-y-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h1 className="text-red-600 font-semibold text-xl text-center">
              {languageHelper("Alert!")}
            </h1>
            <p className="text-center text-gray-700">
              {languageHelper("Are you sure you want to delete this gift card?")}
            </p>

            <div className="flex justify-center gap-4 pt-2">
              <button
                onClick={() => setDeleteId(null)}
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded hover:bg-gray-400 font-medium"
              >
                {languageHelper("No")}
              </button>
              <button
                onClick={() => handleDelete(deleteId)}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 font-medium"
              >
                {languageHelper("Yes")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GiftCards;
